import type { PropsWithChildren, ReactNode } from 'react';
import { useCallback, createContext, useContext } from 'react';

import type { FeatureFlagName } from './flags';

export const FeatureFlagContext = createContext<FeatureFlagName[]>([]);

interface FeatureFlagProviderProps {
  flags?: FeatureFlagName[];
}

export const FeatureFlagProvider = ({
  children,
  flags = [],
}: PropsWithChildren<FeatureFlagProviderProps>) => (
  <FeatureFlagContext.Provider value={flags}>
    {children}
  </FeatureFlagContext.Provider>
);

/**
 * Checks whether a given dev feature flag is enabled. Returns true for a falsy
 * flag name, which is assumed to mean that no flag is required.
 *
 * In React, prefer the {@link useFeatureFlag} hook instead, which calls this
 * internally. This function is provided for use outside of React, to ensure we
 * apply the same logic.
 */
export const hasFeatureFlag = (
  featureFlags: FeatureFlagName[],
  name?: FeatureFlagName,
) => {
  // Handle possible falsy name here to avoid unnecessary logic in consumers
  // i.e. undefined implies that a flag is not required
  if (!name) return true;

  return featureFlags.includes(name);
};

export const useFeatureFlags = () => {
  const featureFlags = useContext(FeatureFlagContext);

  const isEnabled = useCallback(
    (name?: FeatureFlagName) => hasFeatureFlag(featureFlags, name),
    [featureFlags],
  );

  return {
    featureFlags,
    hasFeatureFlag: isEnabled,
  };
};

/**
 * Returns a boolean indicating whether a given feature flag is enabled
 */
export const useFeatureFlag = (name?: FeatureFlagName) =>
  useFeatureFlags().hasFeatureFlag(name);

export interface FeatureFlagProps {
  name: FeatureFlagName;
  then: ReactNode;
  else?: ReactNode;
}

/**
 * Conditionally render components based on the state of a given feature flag
 */
const FeatureFlag = ({
  name,
  then: thenExpr,
  else: elseExpr = null,
}: FeatureFlagProps) => {
  const hasFeature = useFeatureFlag(name);

  return <>{hasFeature ? thenExpr : elseExpr}</>;
};

export default FeatureFlag;
